import React, { useState, useMemo } from 'react';
import { notification, Spin, Button, Switch } from 'antd';
import { ClockCounterClockwise, ListBullets, X, Warning } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import designServices, { RetrieveSystemDesignResponse } from '../../../../services/oneButtonServices/designServices';
import DesignLossesModal from './DesignLossesModal';
import DashboardModal from './DashboardModal';
import './oneButton-css/DesignArrayModal.css';
import {
    type ProductDesignsEmbeddedBridgeProps,
    useProductDesignsEmbeddedFooter,
} from './productDesignsEmbeddedFooter';
import { enableManualMode } from './enableManualMode';
import { EquipmentOverridePanel } from './EquipmentOverridePanel';

function formatVal(v: unknown): string {
    if (v == null || v === '') return '—';
    if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '—';
    return String(v);
}

export interface DesignArrayModalProps {
    open: boolean;
    onClose: () => void;
    systemDesign: RetrieveSystemDesignResponse | null;
    designImageUrl: string | null;
    designImageName?: string;
    productionKwh: number | string | null;
    systemSizeKw: number | string | null;
    multiplier: number | null;
    systemDesignLosses: any;
    solarProductFinancePartner?: string | null;
    onOpenLosses?: () => void;
    onOpenDesignHistoryV2?: () => void;
    onOpenDesignHistory?: () => void;
    /** Render body only (no DashboardModal shell) for ProductDesignsModal tabs */
    embedded?: boolean;
}

export type DesignArrayModalPropsWithEmbedded = DesignArrayModalProps & ProductDesignsEmbeddedBridgeProps;

export const DesignArrayModal: React.FC<DesignArrayModalPropsWithEmbedded> = ({
    open,
    onClose,
    systemDesign,
    systemDesignLosses,
    solarProductFinancePartner,
    multiplier,
    systemSizeKw,
    onOpenDesignHistoryV2,
    onOpenDesignHistory,
    embedded = false,
    embeddedActive,
    onEmbeddedFooterChange,
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    const equipmentMasterData = useOneButtonStore((s) => s.equipmentMasterData);
    const setSystemDesign = useOneButtonStore((s) => s.setSystemDesign);
    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const canManual = enableManualMode(systemDesign, onebuttonCapabilities);
    const [manualMode, setManualMode] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [lossesOpen, setLossesOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [noticeAPI, noticeContext] = notification.useNotification({ maxCount: 4, stack: false });

    const designArrays = useMemo(() => {
        const arr = systemDesign?.DesignArray;
        if (!Array.isArray(arr) || arr.length === 0) return [];
        return arr.map((item) => (item && typeof item === 'object' ? (item as Record<string, any>) : {}));
    }, [systemDesign]);

    const selectedArray = designArrays[selectedIndex] ?? null;

    const getRackingPartNames = (ids: any[] | null | undefined): string => {
        if (!Array.isArray(ids) || !equipmentMasterData?.RackingParts) return '';
        return ids
            .map(id => {
                const match = equipmentMasterData.RackingParts.find((p: any) => p.Racking_Parts_Id === id || p.Racking_Part_Id === id);
                return match ? match.Racking_Part_Name : `Unknown (${id})`;
            })
            .join(', ');
    };

    const actualFinancePartner = useMemo(() => {
        if (solarProductFinancePartner) return solarProductFinancePartner;
        const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        const solarProd = products.find((p: any) => p?.Product_Type_Name?.toLowerCase() === 'solar');
        return solarProd?.Finance_Partner;
    }, [solarProductFinancePartner, opportunityDetails]);

    const isLightReachMet = (tilt: any): boolean => {
        if (!actualFinancePartner || !actualFinancePartner.toLowerCase().includes('lightreach')) return true;
        const t = Number(tilt);
        return Number.isFinite(t) && t >= 5 && t <= 50;
    };

    const hasAnyLightReachTiltError = useMemo(() => {
        if (!actualFinancePartner || !actualFinancePartner.toLowerCase().includes('lightreach')) return false;
        return designArrays.some((arr) => {
            const t = Number(arr.Tilt);
            return Number.isFinite(t) && (t < 5 || t > 50);
        });
    }, [designArrays, actualFinancePartner]);

    const isSolarAvailabilityLow = useMemo(() => {
        return designArrays.some((arr) => {
            const sa = arr.SolarAvailability;
            return sa != null && Number(sa) < 50;
        });
    }, [designArrays]);

    const haveSolarIGS = useMemo(() => {
        return !!actualFinancePartner && actualFinancePartner.toLowerCase().includes('igs');
    }, [actualFinancePartner]);

    const largeArrayData = useMemo(() => {
        if (!designArrays || designArrays.length === 0) return null;
        let maxArr = designArrays[0];
        let maxIdx = 0;
        for (let i = 1; i < designArrays.length; i++) {
            if (Number(designArrays[i].Quantity || 0) > Number(maxArr.Quantity || 0)) {
                maxArr = designArrays[i];
                maxIdx = i;
            }
        }
        return { array: maxArr, index: maxIdx + 1 };
    }, [designArrays]);

    const hasSolarGoodleap = useMemo(() => {
        const fromOpportunity = !!actualFinancePartner && actualFinancePartner.toLowerCase().replace(/\s+/g, '').includes('goodleap');
        const fromSystemDesign = systemDesign?.OpportunityProductsFinanceInfo?.some(
            (p: any) => p?.Finance_Partner?.toLowerCase().replace(/\s+/g, '').includes('goodleap') && p?.Product_Type_Name == 'Solar'
        );
        return fromOpportunity || !!fromSystemDesign;
    }, [actualFinancePartner, systemDesign]);

    const validationMessage = useMemo(() => {
        if (!hasSolarGoodleap) return '';
        
        const stateId = systemDesign?.SolarUtility?.StateId || opportunityDetails?.OpportunityProperty?.State;
        const lowerState = ['CT', 'MA', 'MD', 'ME', 'NH', 'NJ'].includes(stateId);
        const minMult = lowerState ? 0.7 : 0.8;

        const adjustErrorMessage = 'Please adjust the Design to generate a valid Good Leap quote.';
        let multiplierError = '';
        if (multiplier != null && (Number(multiplier) < minMult || Number(multiplier) > 2.0)) {
            multiplierError = `The Multiplier for this system does not fall within the required Good Leap range (${minMult} - 2).`;
        }

        let systemSizeError = '';
        const numericSystemSize = systemSizeKw ? parseFloat(systemSizeKw.toString().replace('kWh', '').trim()) : 0;
        if (numericSystemSize && (numericSystemSize < 3.0 || numericSystemSize > 25.0)) {
            systemSizeError = `The System Size for this system does not fall within the required Good Leap range of (3.0 kWh - 25.0 kWh).`;
        }

        if (multiplierError && systemSizeError) {
            return `${multiplierError} ${systemSizeError} ${adjustErrorMessage}`;
        }
        
        return (multiplierError || systemSizeError)
            ? `${multiplierError} ${systemSizeError} ${adjustErrorMessage}`.trim()
            : '';
    }, [hasSolarGoodleap, systemDesign, opportunityDetails, multiplier, systemSizeKw]);

    const renderAlert = (msg: string | null | undefined, key: string) => {
        if (!msg) return null;
        return (
            <div key={key} style={{ 
                display: 'flex', alignItems: 'center', gap: '8px', 
                padding: '8px 12px', borderRadius: '8px',
                background: 'rgba(254, 242, 242, 0.9)', 
                border: '1px solid rgba(239, 68, 68, 0.2)', 
                color: '#b91c1c', fontSize: '0.75rem', fontWeight: 600, 
                lineHeight: 1.35
            }}>
                <Warning size={16} weight="fill" color="#ef4444" style={{ flexShrink: 0 }} />
                <span>{msg}</span>
            </div>
        );
    };

    const detailRows = useMemo(() => {
        if (!selectedArray) return [];
        const rows: { label: string; value: any; subValue?: string; error?: boolean; errorMessage?: string; color?: string }[] = [];

        // Lookups
        const moduleManufacturerId = selectedArray.ModuleManufacturerId || selectedArray.Module_Manufacturer_Id;
        const manufacturer = equipmentMasterData?.SolarManufacturers?.find((m: any) => Number(m.Value) === Number(moduleManufacturerId))?.Text;
        const moduleManufacturerName = selectedArray.ModuleManufactuterName || selectedArray.Module_Manufacturer_Name || manufacturer || selectedArray.ModuleManufacturer || '—';

        const moduleModelId = selectedArray.ModuleModel || selectedArray.Module_Model_Id;
        const model = equipmentMasterData?.ModuleModels?.find((m: any) => Number(m.Value) === Number(moduleModelId))?.Text;
        const moduleModelName = selectedArray.ModuleModelName || selectedArray.Module_Model_Name || model || selectedArray.ModuleModel || '—';

        // Module
        rows.push({ label: 'Module Manufacturer', value: moduleManufacturerName });
        rows.push({ label: 'Module Model', value: moduleModelName });
        rows.push({ label: 'Module Wattage', value: formatVal(selectedArray.ModuleWattage) });

        // Inverters
        const inverters = selectedArray.Inverters;
        if (Array.isArray(inverters) && inverters.length > 0) {
            inverters.forEach((inv: any, i: number) => {
                const labelSuffix = inverters.length > 1 ? ` ${i + 1}` : '';
                rows.push({ label: `Inverter Manufacturer${labelSuffix}`, value: inv.ManufacturerName || inv.Manufacturer || '—' });
                rows.push({ label: `Inverter Model${labelSuffix}`, value: inv.Model || '—' });
                rows.push({ label: `Inverter Quantity${labelSuffix}`, value: formatVal(inv.Quantity) });
            });
            rows.push({ label: 'Optimizer', value: inverters[0].OptimizerModel || '—' });
        } else {
            rows.push({ label: 'Inverter Manufacturer', value: '—' });
            rows.push({ label: 'Inverter Model', value: '—' });
            rows.push({ label: 'Inverter Quantity', value: '—' });
            rows.push({ label: 'Optimizer', value: '—' });
        }

        // Racking
        rows.push({ label: 'Racking', value: selectedArray.RackingModel || '—' });
        rows.push({ label: 'Racking Types', value: Array.isArray(selectedArray.RackingType) ? selectedArray.RackingType.join(', ') : (selectedArray.RackingType || '—') });
        rows.push({ label: 'Racking Parts', value: getRackingPartNames(selectedArray.RackingParts) || '—' });

        // Physics
        const tiltMet = isLightReachMet(selectedArray.Tilt);
        rows.push({ 
            label: 'Tilt', 
            value: formatVal(selectedArray.Tilt),
            error: !tiltMet,
            errorMessage: !tiltMet ? 'Lightreach requires pitch for an array to be between 5° and 50°, please return to the design system and adjust the affected arrays.' : undefined
        });
        rows.push({ label: 'Azimuth', value: formatVal(selectedArray.Azimuth) });
        rows.push({ label: 'Module Quantity', value: formatVal(selectedArray.Quantity) });

        // Solar Availability
        const sa = selectedArray.SolarAvailability;
        const lowSa = sa != null && Number(sa) < 50;
        rows.push({ 
            label: 'Solar Availability', 
            value: formatVal(sa),
            color: lowSa ? 'red' : undefined,
            error: lowSa,
            errorMessage: lowSa ? 'Solar Availability too low, acceptable range is 50-100,Adjust Genesis Design.' : undefined
        });

        rows.push({ label: 'Building Type', value: (selectedArray.BuildingType && selectedArray.BuildingType !== 'Common') ? selectedArray.BuildingType : 'Roof Mount' });

        return rows;
    }, [selectedArray, equipmentMasterData]);   

    const lossesActions = (
        <div className="obd-design-array__actions">
            <div className="obd-design-array__actions-group" role="group" aria-label="Solar design tools">
                {onOpenDesignHistoryV2 ? (
                    <button
                        type="button"
                        className="obd-merged-system__chip-btn"
                        title="Design history"
                        aria-label="Design history"
                        onClick={onOpenDesignHistoryV2}
                    >
                        <ClockCounterClockwise size={16} weight="bold" aria-hidden />
                        <span>Design history</span>
                    </button>
                ) : null}
                {onOpenDesignHistory ? (
                    <button
                        type="button"
                        className="obd-merged-system__chip-btn"
                        title="oneDRAW History"
                        aria-label="oneDRAW History"
                        onClick={onOpenDesignHistory}
                    >
                        <ClockCounterClockwise size={16} weight="bold" aria-hidden />
                        <span>oneDRAW History</span>
                    </button>
                ) : null}
                {systemDesignLosses != null ? (
                    <button
                        type="button"
                        className="obd-design-array__losses-btn"
                        onClick={() => setLossesOpen(true)}
                        disabled={systemDesignLosses.LossesSource == 'EnFin'}
                    >
                        <ListBullets size={18} weight="bold" aria-hidden />
                        <span> {systemDesignLosses.LossesSource ? (systemDesignLosses.LossesSource + ' Design') : 'System Design Losses'}</span>
                    </button>
                ) : null}
            </div>
        </div>
    );

    const validationAlerts = (hasAnyLightReachTiltError || (hasSolarGoodleap && validationMessage) || isSolarAvailabilityLow || (haveSolarIGS && largeArrayData && Number(largeArrayData.array.Quantity) > 37)) ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', marginBottom: '12px' }}>
            {hasAnyLightReachTiltError && renderAlert('One or more arrays have a pitch that is not between 5° and 50°. Lightreach requires pitch for an array to be between 5° and 50°, please return to the design system and adjust the affected arrays.', 'lightreach')}
            {hasSolarGoodleap && validationMessage && renderAlert(validationMessage, 'goodleap')}
            {isSolarAvailabilityLow && renderAlert('One or more arrays have solar availability below 50%. External finance partners will not accept this data when calculating annual production. Please return to the design system and adjust the affected arrays to exceed 50% availability.', 'solar-availability')}
            {haveSolarIGS && largeArrayData && Number(largeArrayData.array.Quantity) > 37 && renderAlert(`**IMPORTANT** IGS Only allows one inverter per array, hence there can be no more than 37 modules on an array. Array ${largeArrayData.array.Index || largeArrayData.index} has ${largeArrayData.array.Quantity} Modules, please update the design so that the Array has 37 or less modules.`, 'igs')}
        </div>
    ) : null;

    const lossesFooter = (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {validationAlerts}
            {lossesActions}
        </div>
    );

    const productDesignsSolarFooter = (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {validationAlerts}
            <div className="obd-product-designs-modal__footer-bar">
                <div className="obd-product-designs-modal__footer-start">{lossesActions}</div>
                <div className="obd-product-designs-modal__footer-end">
                    <Button icon={<X size={14} weight="bold" />} onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );

    useProductDesignsEmbeddedFooter(
        embedded,
        embeddedActive,
        open,
        productDesignsSolarFooter,
        onEmbeddedFooterChange,
    );

    const arrayBody = (
        <div className="obd-design-array obd-design-array--fold-only" style={{ position: 'relative' }}>
            <section className="obd-design-array__main" aria-label="Array details">
                {manualMode && canManual ? (
                    <EquipmentOverridePanel
                        designArrays={designArrays}
                        onCloseManual={() => setManualMode(false)}
                    />
                ) : designArrays.length === 0 ? (
                    <p className="obd-design-array__empty">No array data available.</p>
                ) : selectedArray ? (
                    <>
                        <div className="obd-design-array__detail-header">
                            <div className="obd-design-array__detail-heading-wrap">
                                <div className="obd-design-array__title-row">
                                    <h3 className="obd-design-array__detail-heading obd-design-array__selected-name">
                                        ARRAY {selectedIndex + 1}
                                    </h3>
                                    {canManual && (
                                        <Switch
                                            checked={false}
                                            checkedChildren="Manual"
                                            unCheckedChildren="Automatic"
                                            onChange={(checked) => { if (checked) setManualMode(true); }}
                                        />
                                    )}
                                </div>
                                {designArrays.length > 1 && (
                                    <div className="obd-design-array__array-tags-wrap" role="tablist" aria-label="Select array">
                                        {designArrays.map((_, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                role="tab"
                                                aria-selected={selectedIndex === i}
                                                className={`obd-design-array__array-tag ${selectedIndex === i ? 'obd-design-array__array-tag--active' : ''}`}
                                                style={
                                                    (() => {
                                                        const arr = designArrays[i];
                                                        const isInvalid = actualFinancePartner && actualFinancePartner.toLowerCase().includes('lightreach') && Number.isFinite(Number(arr.Tilt)) && (Number(arr.Tilt) < 5 || Number(arr.Tilt) > 50);
                                                        return isInvalid ? { border: '2px solid red' } : {};
                                                    })()
                                                }
                                                onClick={() => setSelectedIndex(i)}
                                            >
                                                <span className="obd-design-array__array-tag-label">ARRAY {i + 1}</span>
                                                <span className="obd-design-array__array-tag-index">{i + 1}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="obd-design-array__detail-grid">
                            {(() => {
                                const cells = detailRows.map((row, idx) => (
                                    <div key={idx} className="obd-design-array__cell">
                                        <span className="obd-design-array__cell-label">{row.label}</span>
                                        <div className="obd-design-array__cell-value-wrap">
                                            <span
                                                className={`obd-design-array__cell-value ${row.error ? 'obd-design-array__cell-value--error' : ''}`}
                                                style={row.color ? { color: row.color } : {}}
                                            >
                                                {row.value}
                                            </span>
                                            {row.errorMessage && (
                                                <div className="obd-design-array__error-message" style={{ display: 'block', marginTop: '4px' }}>
                                                    {row.errorMessage}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ));
                                const rows: React.ReactNode[] = [];
                                for (let i = 0; i < cells.length; i += 3) {
                                    rows.push(
                                        <div key={i} className="obd-design-array__detail-row">
                                            {cells.slice(i, i + 3)}
                                        </div>
                                    );
                                }
                                return rows;
                            })()}
                        </div>
                    </>
                ) : null}
            </section>
            {saving && (
                <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    background: theme === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.4)',
                    backdropFilter: 'blur(1px)', zIndex: 1000, borderRadius: 16
                }}>
                    <Spin />
                    <span style={{ fontWeight: 500, color: theme === 'dark' ? 'rgba(248, 250, 252, 0.98)' : '#1e293b' }}>Loading details…</span>
                </div>
            )}
        </div>
    );

    const lossesChild = (
        <DesignLossesModal
            open={lossesOpen}
            onClose={() => setLossesOpen(false)}
            losses={systemDesignLosses as any}
        />
    );

    if (embedded) {
        return (
            <>
                {noticeContext}
                <div className="obd-design-array-modal-embedded">{arrayBody}</div>
                {lossesChild}
            </>
        );
    }

    return (
        <>
            <DashboardModal
                open={open}
                onClose={() => !saving && onClose()}
                title="Design array"
                footer={lossesFooter}
                width={1480}
                centered
                maskClosable={false}
                theme={theme}
                destroyOnClose
                className={`obd-design-array-modal obd-theme-${theme}`}
                rootClassName={`obd-design-array-modal-root obd-theme-${theme}`}
            >
                {noticeContext}
                {arrayBody}
            </DashboardModal>
            {lossesChild}
        </>
    );
};

export default DesignArrayModal;
